import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { AuthenticationFailedError } from "@/auth/AuthError";
import { useNavigate } from "react-router";

interface OrgLoginStartProps {
  organizationId?: string | null;
  organizationName?: string | null;
  organizationError?: string | null;
}

export const LoginStart = ({
  organizationId,
  organizationError,
}: OrgLoginStartProps) => {
  const { startLogin, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [step, setStep] = useState<"email" | "otp">("email");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(
    organizationError ?? null
  );
  const otpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step !== "otp") {
      return;
    }
    const id = requestAnimationFrame(() => {
      otpInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [step]);

  const handleStart = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setErrorMessage("Enter your email address to continue.");
      return;
    }

    if (organizationError) {
      setErrorMessage(organizationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await startLogin(normalizedEmail, organizationId ?? undefined);
      setEmail(normalizedEmail);
      setStep("otp");
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (codeOverride?: string) => {
    const normalizedCode = (codeOverride ?? otpCode).trim();
    if (!normalizedCode) {
      setErrorMessage("Enter the verification code we sent to your email.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await verifyOtp(email, normalizedCode);
      navigate("/", { replace: true });
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setOtpCode("");
    setErrorMessage(organizationError ?? null);
    setStep("email");
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        {step === "email" ? (
          <>
            <h1 className="text-3xl font-semibold tracking-tight">
              Welcome back
            </h1>
            <p className="mt-3 text-sm text-gray-600">
                Sign in to Claire with your university email
            </p>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-semibold tracking-tight">
              Check your email
            </h1>
            <p className="mt-3 text-sm text-gray-600">
              Enter the 6-digit code we sent you.
            </p>
          </>
        )}
      </div>

      <div className="space-y-4">
        {step === "email" && (
          <div className="space-y-2">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@school.edu"
              value={email}
              disabled={isSubmitting}
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  void handleStart();
                }
              }}
            />
          </div>
        )}

        {step === "otp" && (
          <div className="space-y-2">
            <Label htmlFor="otp" className="sr-only">
              Verification code
            </Label>
            <InputOTP
              ref={otpInputRef}
              id="otp"
              className="flex w-full justify-between items-center"
              maxLength={6}
              pattern={REGEXP_ONLY_DIGITS}
              inputMode="numeric"
              autoComplete="one-time-code"
              value={otpCode}
              required
              containerClassName="gap-4"
              disabled={isSubmitting}
              onChange={(value) => setOtpCode(value)}
              onComplete={(value) => void handleVerify(value)}
            >
              <InputOTPGroup className="gap-2.5 *:data-[slot=input-otp-slot]:h-14 *:data-[slot=input-otp-slot]:w-11 *:data-[slot=input-otp-slot]:rounded-md *:data-[slot=input-otp-slot]:border *:data-[slot=input-otp-slot]:text-xl">
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup className="gap-2.5 *:data-[slot=input-otp-slot]:h-14 *:data-[slot=input-otp-slot]:w-11 *:data-[slot=input-otp-slot]:rounded-md *:data-[slot=input-otp-slot]:border *:data-[slot=input-otp-slot]:text-xl">
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
        )}

        {errorMessage && (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {errorMessage}
          </p>
        )}

        {step === "email" ? (
          <Button
            onClick={() => void handleStart()}
            className="w-full"
            disabled={isSubmitting || !!organizationError}
          >
            {isSubmitting ? "Sending code..." : "Continue"}
          </Button>
        ) : (
          <div className="space-y-3">
            <Button
              onClick={() => void handleVerify()}
              className="w-full"
              disabled={isSubmitting || otpCode.length !== 6}
            >
              {isSubmitting ? "Verifying..." : "Verify"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              disabled={isSubmitting}
              onClick={handleReset}
            >
              Use a different email
            </Button>
          </div>
        )}
      </div>

      <div className="text-center text-xs text-gray-500">
        By signing in, you agree to our terms of service and privacy policy.
      </div>
    </div>
  );
};

function getErrorMessage(error: unknown): string {
  if (error instanceof AuthenticationFailedError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
}
